/** @format */
import NextImage, { ImageProps } from 'next/image';
import React, { FunctionComponent } from 'react';

import { useFallback } from '@toucan/client/shared/hooks/use-fallback';
import {
    ResponsiveLoaderType,
    useResponsiveLoader,
} from '@toucan/client/shared/hooks/use-responsive-loader';

import { isString } from '@toucan/client/blocks/components/responsive-image/helpers/type-guards';

export type ResponsiveImageProps = Omit<ImageProps, 'sizes' | 'loader'> & {
    'data-testid'?: string;
    sizes: Record<string, string>;
    fallbackSrc?: string;
    loader?: ResponsiveLoaderType;
};

function getSizeAttribute(sizes: ResponsiveImageProps['sizes']) {
    try {
        const formattedSizes = Object.entries(sizes).map(
            ([mediaQuery, width]) => {
                if (mediaQuery === 'default') {
                    return width;
                }

                return `${mediaQuery} ${width}`;
            }
        );

        return formattedSizes.join(',');
    } catch (error) {
        console.error('Unable to build sizes attribute');
        return '100vw';
    }
}

export const ResponsiveImage: FunctionComponent<ResponsiveImageProps> = ({
    fallbackSrc,
    sizes,
    src,
    loader: loaderType,
    ...nextImageProps
}) => {
    const { loader, isLoaderEnabled } = useResponsiveLoader(loaderType);

    const isCloudflare =
        loaderType === ResponsiveLoaderType.CLOUDFLARE && isLoaderEnabled;

    const sources = (
        isCloudflare
            ? // When Cloudflare is enabled we will make a second request
              // for the same image, but directly to the media service
              // (see `unoptimized` variable for more info).
              [src, src, fallbackSrc]
            : [src, fallbackSrc]
    ).filter(isString);

    const { currentSource, handleError, isFallback } = useFallback(sources);

    const formattedSizes =
        isFallback || !sizes ? undefined : getSizeAttribute(sizes);
    const unoptimized = isFallback || !isLoaderEnabled || !sizes;

    if (!currentSource) {
        return null;
    }

    return (
        <NextImage
            {...nextImageProps}
            src={currentSource}
            onError={handleError}
            sizes={formattedSizes}
            unoptimized={unoptimized}
            loader={loader}
        />
    );
};
